/**
 * FINALIZAR E PUBLICAR — dois eixos independentes, e o ato de publicar.
 *
 * Dez pontos do T2 vivem aqui, e o assunto nasceu de um acidente: eles estavam conferindo a
 * biblioteca quando um deles diz *"mas é o seguinte, nenhum dos seus está finalizado"* — e aí
 * percebem que finalizar não existe.
 *
 *   T2.55 "como é que finaliza? NÃO TEM O PROCESSO DE FINALIZAR."
 *   T2.56 "você poderia colocar uma CHAVINHA, né, se tá terminado ou não, se é para
 *          disponibilizar público ou não"              <- duas chaves, ditas aos 13:41
 *   T2.61 "são duas questões (...) ou você publica ou não publica. E essa questão do finalizado,
 *          se você finalizar, tá encerrado, é o trabalho final, não quero editar mais, e vai
 *          dessa forma, SEJA PUBLICADO OU NÃO. Ou se ainda está em andamento."
 *
 * ── O FORMATO TINHA UM EIXO ONDE ELE PEDIU DOIS ─────────────────────────────────────────────
 *
 * `Publicacao.estado` era `"rascunho" | "publicado"` — UM eixo, dois valores, misturando as duas
 * perguntas. `finalizado` é o segundo eixo, e "seja publicado ou não" é o que torna as quatro
 * combinações obrigatórias:
 *
 *                    em andamento              finalizado
 *   não publicado    trabalhando sozinho       pronto, guardado só para mim
 *   publicado        público e ainda mudando    público e encerrado (T2.58)
 *
 * ── QUEM A TRAVA DE "FINALIZADO" TRANCA, E ESSA É A PARTE QUE SE ERRA ───────────────────────
 *
 * São duas travas de naturezas diferentes, e o dossiê as separa:
 *
 *   T2.62  "se estiver em andamento e se você optar por deixar público, que ninguém possa editar
 *           ALÉM DE VOCÊ"            -> trava contra TERCEIROS
 *   T2.60  "aquela pessoa NÃO TEM MAIS COMO efetivamente editar aquele quadrinho depois"
 *   T2.58  "para com isso conseguir disponibilizar para todo mundo SEM NINGUÉM EDITAR EM CIMA"
 *                                    -> trava contra O PRÓPRIO AUTOR
 *
 * A primeira JÁ EXISTIA desde 09/09: `firestore.rules` exige `resource.data.autor.id ==
 * request.auth.uid` em todo update da biblioteca. Medi isso ao ler o dossiê, e registrei no
 * `CONTEXTO-PL-T2.7.md`. A segunda é o oposto do que as regras faziam — elas existiam para
 * PERMITIR que o autor escrevesse.
 *
 * ── A DÚVIDA D2, DECIDIDA COM A AUTONOMIA DE 2026-09-12 ─────────────────────────────────────
 *
 * As duas gravações do mesmo segundo se invertem: o A2 traz *"você NÃO PODE fazer aquele
 * quadrinho e subir um novo"*, o A3 *"mas você PODE FAZER DELETAR aquele quadrinho e subir um
 * novo"*. Nada no material desempata.
 *
 * **Decidi pela leitura do A3**, e o argumento mais forte não é a qualidade da gravação — é de
 * produto: é trabalho de criança em plataforma escolar, e um estado irreversível ali significa
 * que um clique errado no oitavo ano não tem desfazer. A saída que ele descreve preserva o
 * sentido de finalizar (ninguém edita em cima) e ainda dá saída a quem se arrependeu.
 *
 * **Consequência que fica registrada: "desfinalizar" NÃO existe.** A saída é deletar e subir
 * outro. Se existisse, "finalizado" seria um estado que se desfaz, e a pergunta "quem pode
 * desfazer" nasceria junto — sem nada no pedido que a responda.
 */
import { estadoAoPublicar } from "../validacao/a-conta-de-validacao";
import { Categoria, DocumentoHQ, Publicacao, Serie } from "../api/documento.types";

/** O que o formulário de publicar decide. É o par de chavinhas do T2.56, mais a prateleira. */
export interface EscolhaDePublicacao {
  publicado: boolean;
  /**
   * Quem está publicando é uma conta de validação?
   *
   * Entra na escolha, e não numa checagem dentro de `publicacaoDe`, porque esta função é pura e
   * precisa continuar sendo: a decisão de conter um quadrinho não pode depender de uma chamada de
   * rede ter dado certo. Quem lê a marca é a tela, uma vez, antes de montar a escolha.
   */
  deValidacao?: boolean;
  finalizado: boolean;
  categoria: Categoria;
  serie?: Serie;
}

/**
 * ── AQUI MORAVA `TETO_POR_PAGINA = 700 * 1024`, E ELE FOI REMOVIDO DE PROPÓSITO (`T6.6`) ─────
 *
 * Esta lápide existe para impedir o conserto errado. Quem chegar aqui pelo `T6.6` — *"tem limite
 * de tamanho de página, eu não consigo publicar, ficou 1.20 MB, tem que ser aumentado
 * urgentemente, no mínimo 5 MB"* — vai querer subir um número. **Subir o número não funciona**, e
 * isso foi medido em 17/09 contra o emulador do próprio projeto:
 *
 * ```
 *   1229 KiB (a página dele)  ->  HTTP 400  "longer than 1048487 bytes"
 *   5120 KiB (o que ele pediu) ->  HTTP 400  o mesmo
 * ```
 *
 * O limite é de quem guarda, não nosso. Com o teto em 5 MB o publicar passaria pela nossa
 * checagem e morreria no meio da gravação, com a ficha já escrita — o defeito do `T4.13`, que
 * `plano-de-publicacao.ts` existe para impedir.
 *
 * A decisão inteira, a medição e o caminho que funciona (cada imagem vira documento irmão da
 * página) estão em **`imagens-em-documentos.ts`**. `pesoDaPagina`, abaixo, continua aqui porque a
 * tela ainda mostra o peso.
 */

/** Monta o campo `publicacao` do documento a partir da escolha. */
export function publicacaoDe(escolha: EscolhaDePublicacao, instituicaoId?: string): Publicacao {
  const p: Publicacao = {
    categoria: escolha.categoria,
    // O eixo de publicação continua sendo `estado`, que já existia. `rascunho` é "não publicado".
    // ── A CONTA DE VALIDACAO PUBLICA NOUTRO ESTADO, E ISSO E A CONTENCAO INTEIRA ──────
    //
    // `publicado-validacao` existe para NAO ser `publicado`. A biblioteca, a abertura e a regra
    // de leitura ja filtram por `publicado` — as tres fecham sozinhas, sem consulta nova e sem
    // indice novo. E o `firestore.rules` trava os dois sentidos: quem esta em
    // `contas-de-validacao` so pode gravar este estado, e quem nao esta nunca pode.
    estado: estadoAoPublicar(escolha.publicado, escolha.deValidacao === true) as Publicacao["estado"],
    finalizado: escolha.finalizado,
  };
  if (escolha.serie) p.serie = escolha.serie;
  if (instituicaoId) p.instituicaoId = instituicaoId;
  return p;
}

/**
 * Pode editar este quadrinho publicado?
 *
 * Duas condições, e as duas são dele: ser o autor (T2.62) **e** não estar finalizado (T2.58,
 * T2.60). A regra do Firestore afirma a mesma coisa — esta função é a segunda opinião, e é o que
 * permite à tela esconder o botão em vez de oferecer uma ação que vai voltar negada.
 */
export function podeEditarPublicado(doc: DocumentoHQ, uid: string): boolean {
  if (doc.autor?.id !== uid) return false;
  return !doc.publicacao?.finalizado;
}

/**
 * Pode deletar?
 *
 * SIM, inclusive finalizado — é a dúvida D2, decidida. É a única saída de quem finalizou e se
 * arrependeu, e ela é do autor e de mais ninguém.
 */
export function podeDeletarPublicado(doc: DocumentoHQ, uid: string): boolean {
  return doc.autor?.id === uid;
}

/**
 * As duas exibições da biblioteca — T2.60, e é o ponto que a D2 travava.
 *
 * *"temos duas opções de exibição na biblioteca: um que vai fazer os NÃO FINALIZADOS, mas que vão
 * estar distribuídos, ou seja, você pode abrir ele amanhã e o balão de fala ter mudado, qualquer
 * coisa ter mudado; mas você tem um outro tipo de estrutura que é os FINALIZADOS, na qual aquela
 * pessoa não tem mais como efetivamente editar aquele quadrinho depois."*
 *
 * Note o que a distinção dele É: não é "bom e ruim", é **estável e mutável**. Quem abre um item em
 * andamento pode voltar amanhã e achar outra coisa; quem abre um finalizado acha o mesmo. Por isso
 * os rótulos dizem isso, e não "pronto"/"incompleto".
 */
export const EXIBICOES = ["todos", "finalizados", "em-andamento"] as const;
export type Exibicao = (typeof EXIBICOES)[number];

export const ROTULO_EXIBICAO: Record<Exibicao, string> = {
  todos: "Todos",
  finalizados: "Prontos",
  "em-andamento": "Ainda mudando",
};

export const AJUDA_EXIBICAO: Record<Exibicao, string> = {
  todos: "Tudo que foi publicado na plataforma.",
  finalizados: "O autor encerrou. O que você lê hoje é o que vai estar aqui amanhã.",
  "em-andamento": "Publicado e ainda em construção — pode mudar de um dia para o outro.",
};

export function naExibicao(doc: DocumentoHQ, exibicao: Exibicao): boolean {
  if (exibicao === "todos") return true;
  const finalizado = !!doc.publicacao?.finalizado;
  return exibicao === "finalizados" ? finalizado : !finalizado;
}

/**
 * Quanto pesa uma página serializada.
 *
 * `JSON.stringify(...).length` conta UTF-16, e o Firestore cobra em bytes UTF-8 — para base64,
 * que é ASCII, os dois coincidem, e base64 é justamente o que pesa aqui. Para texto acentuado a
 * contagem subestima em alguns bytes, e a folga de 324 KiB até o limite real absorve isso.
 */
export function pesoDaPagina(pagina: unknown): number {
  return JSON.stringify(pagina).length;
}

